import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "src/components/dashboard";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import BookingsTabs from "src/components/bookings/BookingsTabs";
import BookingStatus from "src/components/bookings/BookingStatus";
import WalletTable from "src/components/artisan/WalletTable";
import { BusinessLogoo } from "src/components/partners/details/BusinessLogo";
import { formatDateDmy, numberWithCommas } from "src/utils/helpers";
import { Flex } from "src/components/ui";
import { PaginationProps } from "src/components/ui/Pagination";
import ChangeWithdrawalBankAccount from "src/components/users/ChangeWithdrawalBankModel";
import { useGetArtisanQuery } from "src/store/services/artisanApi";
import userIcon from "../../assets/images/profile/Partner/Icon.png";
import { useCredentialMutation } from "src/store/services/bookingApi";
import { auth } from "src/store/Auth";
import OtpAdmin from "src/components/users/otpAdmin";
import AddNewAccountDetail from "src/components/users/AddNewWithdrawlAccount";
import { useAddOperatingStatePartnerMutation } from "src/store/services/partnerApi";
import { useLazyGetStateQuery } from "src/store/services/partnerApi";
import { useLazyGetCountryQuery } from "src/store/services/partnerApi";
import {
  BusinessDetailsCard,
  GalleryView,
  BusinessBio,
  AdminManagement,
  BusinessLogo,
  StatsCard,
  AccDetTable,
  PartnerDetailsSkeleton,
} from "src/components/partners/details";
import { useLoading } from "src/hooks";
import FinancialOverview from "src/components/partners/FinancialOverview";
import { useGetPartnerProfileQuery } from "src/store/services/partnerApi";
import { useArtisanBookingHistoryQuery } from "src/store/services/bookingApi";
import {
  useSuspendArtisanMutation,
  useUnSuspendArtisanMutation,
} from "src/store/services/artisanApi";
import { toast, ToastContainer } from "react-toastify";
import AdminProfile from "src/service/AdminProfile";
import { setAuthUser } from "src/store/Auth";
import InitiateWithdrawl from "src/components/users/InitiateWithdrawl";
import {
  useLazyGetDeliveryPartnerQuery,
  useGetPartnerCategoryByIdQuery,
} from "src/store/services/partnerApi";

const PartnerDetails = () => {
  const {
    loading: fetchingAuthUser,
    startLoading: startFetchingAuthUser,
    stopLoading: stopFetchingAuthUser,
  } = useLoading(false);

  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ] as const;

  type Day = (typeof days)[number];

  type OperationalHours = {
    [key in Day]: {
      is_open: boolean;
      open: string;
      close: string;
    };
  };

  const [opeFormData, setOpeFormData] = useState<{
    operational_hours: OperationalHours;
  }>({
    operational_hours: {
      monday: { is_open: true, open: "08:00", close: "18:00" },
      tuesday: { is_open: true, open: "08:00", close: "18:00" },
      wednesday: { is_open: true, open: "08:00", close: "18:00" },
      thursday: { is_open: true, open: "08:00", close: "18:00" },
      friday: { is_open: true, open: "08:00", close: "16:00" },
      saturday: { is_open: true, open: "10:00", close: "14:00" },
      sunday: { is_open: false, open: "", close: "" },
    },
  });

  const { authUser } = auth.use();

  const navigate = useNavigate();

  const [showDeliveryCred, setShowDeliveryCred] = useState("");

  const [showImportant, setShowImportant] = useState(true);
  useEffect(() => {
    if (authUser?.role?.includes("centralPartnerManager")) {
      setShowImportant(false);
    }
  }, [authUser]);

  const Auth_ID = authUser._id;

  const [pageNo, setPageNo] = useState(1);
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date;
  });
  const [status, setStatus] = useState("");
  const [endDate, setEndDate] = useState<Date>(new Date());

  const [addAccount, setAddAccount] = useState(false);

  const [openChangeBankModal, setOpenChangeBankModal] = useState(false);
  const [switchFun, setSwitchfun] = useState(false);

  const toggleChangeBankModal = () => {
    if (!addAccount && !switchFun) {
      setOpenChangeBankModal(() => !openChangeBankModal);
    } else {
      setAddAccount(!addAccount);
    }
  };

  const [paginationData, setPaginationData] = useState<PaginationProps>({
    page: 0,
    pageSize: 0,
    total: 0,
    totalPages: 0,
  });
  const { partnerId } = useParams();
  const {
    data: partnerProfile,
    isLoading,
    refetch: refetchPartnerProfile,
  } = useGetPartnerProfileQuery(partnerId as string);
  const { data: partnerBookingsResp } = useArtisanBookingHistoryQuery(
    {
      artisanId: partnerId as string,
      page: pageNo,
      limit: 10,
    },
    { skip: !partnerId },
  );

  const [Credential] = useCredentialMutation();

  const [suspendPartner, { isLoading: isSuspending }] =
    useSuspendArtisanMutation();
  const [unsuspendPartner, { isLoading: isUnsuspending }] =
    useUnSuspendArtisanMutation();

  const [addState, { isLoading: isAdding }] =
    useAddOperatingStatePartnerMutation();

  const [getCountry, { data: country, isError: countryErr }] =
    useLazyGetCountryQuery();

  const [
    getState,
    { data: state, isError: stateErr, isFetching: isStateLoading },
  ] = useLazyGetStateQuery();

  const [isStatePopupOpen, setIsStatePopupOpen] = useState(false);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [removingState, setRemovingState] = useState<string | null>(null);
  const [isoCode, setIsoCode] = useState("");

  const countries = country?.payload?.data;

  const handleOpenStatePopup = () => {
    setSelectedStates([]);
    getCountry(undefined as any); // hook requires an arg even if backend ignores it
    setIsStatePopupOpen(true);
  };

  // once countries load, find the one matching the partner's profile country
  useEffect(() => {
    if (!countries) return;

    const matched = countries.find(
      (c) => c.name === partnerProfile?.payload?.country,
    );

    if (matched) {
      setIsoCode(matched.isoCode);
    }
  }, [countries, partnerProfile]);

  // once isoCode resolves, fetch states for that country
  useEffect(() => {
    if (isoCode) {
      getState(isoCode);
    }
  }, [isoCode]);

  const toggleStateSelection = (stateName: string) => {
    setSelectedStates((prev) =>
      prev.includes(stateName)
        ? prev.filter((s) => s !== stateName)
        : [...prev, stateName],
    );
  };

  const handleRemoveState = async (stateValue: string) => {
  try {
    setRemovingState(stateValue);
    await addState({
      id: partnerId as string,
      data: { states: stateValue, action: "remove" },
    }).unwrap();

    toast.success("Operational state removed successfully");
    refetchPartnerProfile();
  } catch (err: any) {
    toast.error(
      err?.data?.error?.message || "Failed to remove operational state",
    );
  } finally {
    setRemovingState(null);
  }
};

  const handleConfirmAddStates = async () => {
    if (!isoCode || selectedStates.length === 0) return;

    const prefix = isoCode.toLowerCase();

    try {
      for (const name of selectedStates) {
        const value = `${prefix}_${name.toLocaleLowerCase()}`;

        await addState({
          id: partnerId as string,
          data: { states: value, action: "add" },
        }).unwrap();
      }

      toast.success("Operational state added successfully");

      setIsStatePopupOpen(false);
      setSelectedStates([]);
      refetchPartnerProfile();
    } catch (err) {
      // console.error("Failed to add operational states:", err);
      toast.error("Failed to add operational states");
    }
  };

  const [partnerBookings, setPartnerBookings] = useState<BookingsTypes[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isActive, setIsActive] = useState(true);
  const [internalNotes, setInternalNotes] = useState("");
  const [resend, setResend] = useState(false);
  const [resendFun, setResendFun] = useState("");
  const [statusLvl, setStatusLvl] = useState(true);

  // Extract bookings from API response
  useEffect(() => {
    const rows: any[] = partnerBookingsResp?.payload?.data || [];
    setPartnerBookings(rows);
    setPaginationData(partnerBookingsResp?.payload?.meta);
  }, [partnerBookingsResp]);

  const [artisanDetails, setArtisanDetails] = useState<ArtisanProfileDetails>({
    _id: "",
    first_name: "",
    last_name: "",
    email: "",
    occupation: "",
    profile_stage: "",
    rating: 0,
    phone: "",
    status: "",
    tier: 0,
    suspended: false,
    display_picture: "",
    address: {
      house_address: "",
      city: "",
      state: "",
    },
    withdrawal_bank: {
      account_name: "",
      account_number: "",
      bank_name: "",
      bank_code: "",
    },
    createdAt: "",
    updatedAt: "",
    deleted: false,
    can_edit_profile: true,
    can_withdraw: true,
    lastLogin: "",
    meta: null,
    currency_symbol: "",
  });

  // console.log(artisanDetails?.currency_symbol);

  const [referredUsers, setReferredUsers] = useState<ReferredUserProps[]>([]);

  const {
    data: artisanPayload,
    isLoading: isLoadingArtisan,
    refetch,
  } = useGetArtisanQuery(partnerId as string, { skip: !partnerId });

  const [getCredentials, { data, isError }] = useLazyGetDeliveryPartnerQuery();

  useEffect(() => {
    getCredentials({
      id: partnerId,
    });
  }, [getCredentials]);

  const credentials = data?.payload?.credentials || [];

  // console.log(credentials.length);
  // console.log(!isError);

  useEffect(() => {
    const payload: any = artisanPayload?.payload?.data;
    if (!payload) return;
    setArtisanDetails({
      ...payload?.artisan,
      withdrawal_bank: payload?.withdrawal_bank,
    });
    console.log(artisanDetails._id);
    setReferredUsers(payload?.referredUsers || []);
  }, [artisanPayload]);

  // Extract partner data from API response
  const partnerData = useMemo(() => {
    if (!partnerProfile?.payload) return null;

    const profile = partnerProfile.payload;
    const partnerInfo = profile.partner_info || {};
    // setShowDeliveryCred(partnerIn)
    const cat_id = partnerInfo || profile.account_manager;
    const address = profile.address || {};
    const meta = profile.meta || {};

    const decodeBio = (bio?: string) => {
      if (!bio) return "";
      try {
        return decodeURIComponent(bio);
      } catch {
        return bio;
      }
    };

    console.log(opeFormData);
    console.log(profile?.operational_hours);
    setOpeFormData({
      operational_hours: profile?.operational_hours || {
        monday: { is_open: true, open: "08:00", close: "18:00" },
        tuesday: { is_open: true, open: "14:00", close: "18:00" },
        wednesday: { is_open: true, open: "08:00", close: "18:00" },
        thursday: { is_open: true, open: "08:00", close: "18:00" },
        friday: { is_open: true, open: "08:00", close: "16:00" },
        saturday: { is_open: false, open: "10:00", close: "14:00" },
        sunday: { is_open: false, open: "", close: "" },
      },
    });
    console.log(opeFormData);

    const businessBio =
      decodeBio(meta.bio) || partnerInfo.bio || "No bio available";

    const gallery = Array.isArray(meta.socialGallery)
      ? meta.socialGallery
          .map((item: any) =>
            typeof item === "string"
              ? item
              : item?.image_url || item?.url || "",
          )
          .filter(Boolean)
      : [];

    const yearsOfOperation = partnerInfo.years_of_operation;
    const yearsLabel = yearsOfOperation
      ? `${yearsOfOperation} ${yearsOfOperation === 1 ? "year" : "years"}`
      : "N/A";

    const walletBalance = profile.wallet_balance?.balance
      ? `₦${numberWithCommas(profile.wallet_balance.balance)}`
      : "₦0";

    return {
      businessName: profile.business_name || "N/A",
      email: profile.email || "N/A",
      phoneNumber: profile.phone || "N/A",
      contactFirstName: profile.first_name || "N/A",
      contactLastName: profile.last_name || "N/A",
      contactPhoneNumber: partnerInfo.contact_person || profile.phone || "N/A",
      yearsOfOperation: yearsLabel,
      physicalAddress:
        partnerInfo.partner_address || address.house_address || "N/A",
      city: partnerInfo.city || address.city || "N/A",
      stateProvince: partnerInfo.state || address.state || "N/A",
      zipPostalCode: partnerInfo.zip_code || "N/A",
      country: profile.country || "N/A",
      businessHoursOpen: partnerInfo.business_hours_open || "N/A",
      businessHoursClose: partnerInfo.business_hours_close || "N/A",
      isRegistered:
        partnerInfo.is_registered === true
          ? "Yes"
          : partnerInfo.is_registered === false
            ? "No"
            : "N/A",
      rcNumber: partnerInfo.rc_number || "N/A",
      accountStatus: profile.status || "Active",
      dateCreated: profile.createdAt ? formatDateDmy(profile.createdAt) : "N/A",
      assignedAdmin: profile.account_manager?.partnership_manager || "N/A",
      accountManager: profile.account_manager?.source_by || "N/A",
      accountManagerPhone:
        profile.account_manager?.account_manager_phone || "N/A",
      designation: profile.designation || "N/A",
      gender: profile.gender || "N/A",
      internalNotes: "",
      businessBio,
      logo: profile.display_picture || "",
      cover_photo: meta.cover_photo_url || "",
      social_gallery_m: meta.socialGallery,
      social_gallery: [
        ...new Set(
          (meta.socialGallery || [])
            .map((item: any) => item?.category_name)
            .filter(Boolean),
        ),
      ],
      gallery,
      totalBookings: 0,
      walletBalance,
      phoneVersion: profile.phoneVersion || "",
      referralCode: profile.referralCode || "",
      lastLogin: profile.lastLogin ? formatDateDmy(profile.lastLogin) : "N/A",
      withdrawal_bank: profile.referralCode || [],
      canEditProfile: artisanDetails.can_edit_profile,
    };
  }, [partnerProfile]);
  const isSuspended = partnerProfile?.payload?.suspended || false;

  const handleNavigate = (row: any) => {
    navigate(`/bookings/${row?._id}?tabStatus=${row?.status}`);
  };

  const [initiateModalOpen, setInitiateModalOpen] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);

  console.log(
    "socialGallery raw:",
    partnerProfile?.payload?.meta?.socialGallery,
  );
  console.log("social_gallery categories:", partnerData?.social_gallery);

  // Table headers for bookings
  const BookingsTableHeaders = [
    {
      title: "Customer",
      render: (row: BookingsTypes) => (
        <Flex gap="10px" align="center">
          <img
            style={{ width: "40px", height: 40, borderRadius: "50%" }}
            src={row.user_meta?.display_picture || ""}
            alt=""
          />
          {row.user_meta?.first_name} {row.user_meta?.last_name}
        </Flex>
      ),
    },
    {
      title: "Location",
      render: (row: BookingsTypes) =>
        `${
          row.user_meta?.address?.street ||
          row.user_meta?.user_current_address?.street ||
          ""
        }`,
    },
    {
      title: "Start Date",
      render: (row: BookingsTypes) =>
        row.createdAt
          ? new Date(row.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "",
    },
    {
      title: "End Date",
      render: (row: BookingsTypes) =>
        row.endDate
          ? new Date(row.endDate).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "",
    },
    {
      title: "Status",
      render: (row: BookingsTypes) => <BookingStatus status={row["status"]} />,
    },
  ];

  const handleBack = () => {
    if (showImportant) {
      navigate(
        `/partners/${partnerProfile?.payload.partner_info?.category_id}`,
      );
    } else {
      navigate(`/partners/group/${authUser._id}`);
    }
  };

  const handleSuspend = async () => {
    if (!partnerId || !partnerProfile?.payload) return;

    try {
      if (isSuspended) {
        // Unsuspend the partner
        const res: any = await unsuspendPartner({
          artisanId: partnerId,
          data: {},
        }).unwrap();
        toast.success(
          res?.message || "Partner account unsuspended successfully",
        );
      } else {
        // Suspend the partner
        const res: any = await suspendPartner({
          artisanId: partnerId,
          data: {},
        }).unwrap();
        toast.success(res?.message || "Partner account suspended successfully");
      }
      // Refetch partner profile to update status
      refetchPartnerProfile();
    } catch (err: any) {
      toast.error(
        err?.data?.error?.message ||
          `Failed to ${isSuspended ? "unsuspend" : "suspend"} partner account`,
      );
    }
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export partner details");
  };

  const handleEditDetails = () => {
    navigate(`/partners/edit/${partnerId}`);
  };

  const handleDeleteImage = (index: number) => {
    // TODO: Implement delete image functionality
    console.log("Delete image:", index);
  };

  const handleAddImage = () => {
    // TODO: Implement add image functionality
    console.log("Add image");
  };

  const handleStatusChange = (isActive: boolean) => {
    setIsActive(isActive);
    // TODO: Update status in the backend
  };

  const handleNotesChange = (notes: string) => {
    setInternalNotes(notes);
    // TODO: Save notes to the backend
  };

  useEffect(() => {
    if (!artisanDetails?.withdrawal_bank?.account_number) {
      setSwitchfun(true);
    } else {
      setSwitchfun(false);
    }
  }, [artisanDetails?.withdrawal_bank?.account_number]);

  if (isLoading) {
    return (
      <DashboardLayout pageTitle="">
        <PartnerDetailsSkeleton />
      </DashboardLayout>
    );
  }

  if (!partnerData) {
    return (
      <DashboardLayout pageTitle="">
        <div className="p-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-[#7c2ebd] mb-4 hover:opacity-80"
          >
            <ArrowBackIcon fontSize="small" />
            <span className="text-sm font-medium">Back to Total Partners</span>
          </button>
          <div className="text-center py-12">
            <p className="text-gray-600">Partner profile not found</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="">
      <div className="p-6">
        {/* Header */}
        <button
          onClick={handleBack}
          className={`flex items-center gap-2 text-[#7c2ebd] mb-4 hover:opacity-80 `}
        >
          <ArrowBackIcon fontSize="small" />
          <span className="text-sm font-medium">
            {showImportant ? "Back to Total Partners" : "Back to Outlets"}
          </span>
        </button>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-[28px] font-bold text-[#3a3541]">
                {partnerData.businessName} - Profile
              </h1>
              <span className="bg-[#7c2ebd] text-white text-xs font-medium px-3 py-1 rounded-full">
                {partnerData.accountStatus.toUpperCase()}
              </span>
              <div className={showImportant ? "relative" : "hidden"}>
                <span
                  className="flex gap-3 items-center bg-white p-2 rounded-lg"
                  onClick={() => {
                    setResend(!resend);
                  }}
                >
                  Password <img src={userIcon} alt="" />
                </span>
                <div
                  className={`absolute bg-white top-[50px] w-52 ${resend ? "flex" : "hidden"} flex-col shadow-md rounded-lg`}
                >
                  {/* <span
                    className="hover:bg-gray-100 w-full rounded-lg p-2"
                    onClick={() => {
                      const item = "resend";
                      setResendFun(item);
                      setResend(!resend);

                      Credential({ id: partnerId as string, item });
                    }}
                  >
                    Resend Password
                  </span> */}
                  <span
                    className={`hover:bg-gray-100  w-full rounded-lg p-2`}
                    onClick={async () => {
                      const item = "reset";
                      setResendFun(item);
                      setResend(!resend);

                      try {
                        // Call your async function
                        const res: any = await Credential({
                          id: partnerId as string,
                          item,
                        });
                        toast.success(
                          res?.message || "Credential Sent successfully",
                        );
                      } catch (err: any) {
                        toast.error(
                          err?.data?.error?.message ||
                            "Failed to send credential",
                        );
                      }
                    }}
                  >
                    Resend Password
                  </span>
                </div>
              </div>
            </div>
            <p className="text-sm text-[#6b6675]">
              Manage partner details and account settings
            </p>
          </div>

          <div className="flex gap-3 flex-wrap">
            {/* <button
							onClick={handleExport}
							className='bg-white h-10 min-w-[100px] flex items-center justify-center gap-2 text-black rounded-lg px-4 py-2 border border-gray-200 hover:bg-gray-50'
						>
							<FileDownloadOutlinedIcon fontSize='small' />
							<span className='text-sm'>Export</span>
						</button> */}
            {partnerProfile?.payload.partner_info?.category_name ===
              "Delivery" &&
              credentials.length > 0 && (
                <button
                  onClick={() =>
                    navigate(`/edit_delivery_partner/${partnerId}`)
                  }
                  className={`h-10 min-w-[140px] ${
                    showImportant ? "flex" : "hidden"
                  } items-center justify-center gap-2 text-white rounded-lg px-4 py-2 ${"bg-green-600 hover:bg-green-700"} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Edit Delivery Credential
                </button>
              )}
            <button
              onClick={handleSuspend}
              disabled={isSuspending || isUnsuspending}
              className={`h-10 min-w-[140px] ${showImportant ? "flex" : "hidden"}  items-center justify-center gap-2 text-white rounded-lg px-4 py-2 ${
                isSuspended
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-[#dc2626] hover:bg-red-700"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <span className="text-sm">
                {isSuspending || isUnsuspending
                  ? "Processing..."
                  : isSuspended
                    ? "Activate Account"
                    : "Deactivate Account"}
              </span>
            </button>
            <button
              onClick={handleEditDetails}
              className={`bg-[#7c2ebd] h-10 min-w-[120px] ${showImportant ? "flex" : "hidden"} items-center justify-center gap-2 text-white rounded-lg px-4 py-2 hover:bg-purple-700`}
            >
              <span className="text-sm">Edit Details</span>
            </button>
          </div>
        </div>

        {/* Financial Overview */}
        <FinancialOverview artisanId={partnerId as string} />

        {/* Partner Details */}
        <div className="grid grid-rows-1 lg:grid-row-3 gap-6 mt-6">
          {/* Left Column - Business Details */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
            {/* Business Info - 3/4 width */}
            <div className="lg:col-span-9 space-y-6">
              <BusinessDetailsCard
                onClickAction={toggleChangeBankModal}
                businessName={partnerData.businessName}
                email={partnerData.email}
                phoneNumber={partnerData.phoneNumber}
                yearsOfOperation={partnerData.yearsOfOperation}
                physicalAddress={partnerData.physicalAddress}
                city={partnerData.city}
                stateProvince={partnerData.stateProvince}
                zipPostalCode={partnerData.zipPostalCode}
                country={partnerData.country}
                businessHoursOpen={partnerData.businessHoursOpen}
                businessHoursClose={partnerData.businessHoursClose}
                isRegistered={partnerData.isRegistered}
                rcNumber={partnerData.rcNumber}
                account_name={artisanDetails?.withdrawal_bank.account_name}
                account_number={artisanDetails?.withdrawal_bank.account_number}
                bank_name={artisanDetails?.withdrawal_bank.bank_name}
                showImportant={showImportant}
              />

              <GalleryView
                images={partnerData.gallery}
                category_name={partnerData?.social_gallery}
                partnerId={partnerId as string}
                social_gallery={partnerData?.social_gallery_m}
                onAddImage={handleAddImage}
                onDeleteImage={handleDeleteImage}
              />

              <div className="lg:col-span-9 flex items-center justify-between gap-6">
                <BusinessLogoo
                  logo={partnerData.logo}
                  businessName={partnerData.businessName}
                />
                <div
                  className="bg-white p-6 rounded-2xl flex flex-col justify-center items-start gap-6 w-full h-[250px] m-0"
                  style={{ margin: "0 !important" }}
                >
                  <span className="flex flex-col justify-center items-start">
                    <h2
                      className="text-xl text-black"
                      style={{ fontFamily: "Blinker", fontWeight: "600" }}
                    >
                      Business Bio
                    </h2>
                    <p
                      className="text-[#717182] text-[16px] leading-6"
                      style={{ fontFamily: "Blinker" }}
                    >
                      Description and additional information about the business
                    </p>
                  </span>
                  <p
                    className="text-gray-500 px-2 py-3 text-sm bg-gray-50"
                    style={{ fontFamily: "Arimo" }}
                  >
                    {partnerProfile?.payload?.meta?.bio}
                  </p>
                </div>
              </div>

              <div className={`bg-white rounded-lg shadow-sm p-6 `}>
                <div className="">
                  <label className="block text-sm font-medium text-gray-900">
                    Business Operational Hours
                  </label>

                  {days.map((day) => {
                    const currentDay = opeFormData.operational_hours[day];

                    return (
                      <div
                        key={day}
                        className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center border border-gray-200 rounded-lg p-4"
                      >
                        {/* Day */}
                        <div className="font-medium capitalize text-gray-700">
                          {day}
                        </div>

                        {/* Open Toggle */}
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={currentDay.is_open}
                            disabled={true}
                          />
                          <span className="text-sm text-gray-600">Open</span>
                        </label>

                        {/* Open Time */}
                        <input
                          type="time"
                          value={currentDay.open}
                          disabled={true}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
                        />

                        {/* Close Time */}
                        <input
                          type="time"
                          value={currentDay.close}
                          disabled={true}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* <div className={`bg-white rounded-lg shadow-sm p-6 `}>
                <div className="">
                  <label className="block text-sm font-medium text-gray-900">
                    Operational States
                  </label>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {partnerProfile?.payload?.operational_states?.map(
                      (state) => (
                        <span
                          key={state}
                          className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 capitalize"
                        >
                          {state.replace(/^ng_/, "").replace(/_/g, " ")}
                        </span>
                      ),
                    )}

                    {(!partnerProfile?.payload?.operational_states ||
                      partnerProfile?.payload?.operational_states.length ===
                        0) && (
                      <span className="text-sm text-gray-500">
                        No operational states set.
                      </span>
                    )}
                  </div>
                </div>
              </div> */}
             {partnerProfile?.payload?.partner_info?.category_name ===
  "Delivery" && (
  <div className="bg-white rounded-lg shadow-sm p-6">
    <div className="flex items-center justify-between">
      <label className="block text-sm font-medium text-gray-900">
        Operational States
      </label>

      <button
        type="button"
        onClick={handleOpenStatePopup}
        className="text-sm font-medium text-primary hover:underline"
      >
        + Add State
      </button>
    </div>

    <div className="mt-3 flex flex-wrap gap-2">
      {partnerProfile?.payload?.operational_states?.map((state) => (
        <span
          key={state}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 capitalize"
        >
          {state.replace(/^ng_/, "").replace(/_/g, " ")}
          <button
            type="button"
            onClick={() => handleRemoveState(state)}
            disabled={removingState === state}
            className="text-gray-400 hover:text-red-600 disabled:opacity-50"
            title="Remove state"
          >
            {removingState === state ? "…" : "✕"}
          </button>
        </span>
      ))}

      {(!partnerProfile?.payload?.operational_states ||
        partnerProfile?.payload?.operational_states.length === 0) && (
        <span className="text-sm text-gray-500">
          No operational states set.
        </span>
      )}
    </div>
  </div>
)}

              {/* Add State Popup */}
              {isStatePopupOpen && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Add Operational States
                      </h3>
                      <button
                        type="button"
                        onClick={() => setIsStatePopupOpen(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    </div>

                    {countryErr && (
                      <p className="text-sm text-red-600 mb-2">
                        Failed to load country data.
                      </p>
                    )}
                    {stateErr && (
                      <p className="text-sm text-red-600 mb-2">
                        Failed to load states.
                      </p>
                    )}

                    <div className="max-h-72 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-3">
                      {isStateLoading && (
                        <p className="text-sm text-gray-500">
                          Loading states...
                        </p>
                      )}

                      {!isStateLoading &&
                        state?.payload?.data?.map((s) => {
                          const value = `${isoCode?.toLowerCase()}_${s.name}`;
                          const alreadyAdded =
                            partnerProfile?.payload?.operational_states?.includes(
                              value,
                            );
                          return (
                            <label
                              key={s.isoCode}
                              className={`flex items-center gap-2 px-2 py-1 rounded ${
                                alreadyAdded ? "opacity-50" : "hover:bg-gray-50"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={
                                  selectedStates.includes(s.name) ||
                                  alreadyAdded
                                }
                                disabled={
                                  alreadyAdded ||
                                  (selectedStates.length > 0 &&
                                    !selectedStates.includes(s.name))
                                }
                                onChange={() => toggleStateSelection(s.name)}
                              />
                              <span className="text-sm text-gray-700 capitalize">
                                {s.name}
                              </span>
                            </label>
                          );
                        })}

                      {!isStateLoading &&
                        state?.payload?.data?.length === 0 && (
                          <p className="text-sm text-gray-500">
                            No states found.
                          </p>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 mt-5">
                      <button
                        type="button"
                        onClick={() => setIsStatePopupOpen(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={selectedStates.length === 0 || isAdding}
                        onClick={handleConfirmAddStates}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg disabled:opacity-50"
                      >
                        {isAdding
                          ? "Adding..."
                          : `Add ${selectedStates.length || ""} State${selectedStates.length === 1 ? "" : "s"}`}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="lg:col-span-3 space-y-6">
                <StatsCard
                  icon={<BookmarkBorderIcon className="text-gray-700" />}
                  title="Total Orders"
                  value={partnerBookings?.length || 0}
                  onViewDetails={() => {}}
                />

                <div className="flex items-center justify-between w-full p-4 bg-white rounded-lg shadow hover:bg-gray-50 transition cursor-pointer">
                  {/* Left side */}
                  <div className="flex items-center gap-3">
                    <AccountBalanceWalletOutlinedIcon className="text-gray-700" />

                    <div className="flex flex-col text-left">
                      <span className="text-sm text-gray-500">
                        Wallet Balance
                      </span>
                      <span className="text-lg font-semibold text-gray-800">
                        {`${partnerProfile?.payload?.currency_symbol || "₦"}${
                          numberWithCommas(
                            partnerProfile?.payload?.wallet_balance?.balance ??
                              0,
                          ) || "0"
                        }`}
                      </span>
                    </div>
                  </div>

                  {/* Inner button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // prevents triggering parent click
                      setInitiateModalOpen(!initiateModalOpen);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-[#7c2ebd] text-white rounded-lg hover:bg-purple-700 text-sm"
                  >
                    <AccountBalanceWalletOutlinedIcon fontSize="small" />
                    <span>Withdraw</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Account Management - 1/4 width */}
            <div className="lg:col-span-3 space-y-6">
              <AdminManagement
                isActive={!isSuspended}
                handleSuspend={handleSuspend}
                onStatusChange={handleStatusChange}
                dateCreated={partnerData.dateCreated}
                assignedAdmin={partnerData.assignedAdmin}
                accountManager={partnerData.accountManager}
                accountManagerPhone={partnerData.accountManagerPhone}
                designation={partnerData.designation}
                internalNotes={internalNotes}
                onNotesChange={handleNotesChange}
                contactFirstName={partnerData.contactFirstName}
                contactLastName={partnerData.contactLastName}
                contactPhoneNumber={partnerData.contactPhoneNumber}
                gender={partnerData.gender}
                statusLvl={statusLvl}
                setStatusLvl={setStatusLvl}
                phoneVersion={partnerData.phoneVersion}
                referralCode={partnerData.referralCode}
                lastLogin={partnerData.lastLogin}
                can_edit_profile={artisanDetails.can_edit_profile}
                can_withdraw={artisanDetails.can_withdraw}
              />

              <BusinessLogo
                logo={partnerData.cover_photo}
                businessName={partnerData.businessName}
              />
            </div>
          </div>
        </div>

        {/* Bookings and Wallet Section */}
        <div className="mt-12 space-y-~8">
          {/* Bookings Tabs */}
          <BookingsTabs
            rows={partnerBookings}
            BookingsTableHeaders={BookingsTableHeaders}
            title={
              <h2 className="text-xl font-semibold mb-4">Recent Orders</h2>
            }
            className="bg-white rounded-xl p-6 shadow-sm"
            onRowClick={handleNavigate}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            paginationData={paginationData}
            pgn={pageNo}
            setpgn={setPageNo}
            setEndDate={setEndDate}
            setStartDate={setStartDate}
            setStatus={setStatus}
          />

          {/* Wallet Table */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <WalletTable
              rows={
                partnerProfile?.payload?.wallet_transaction?.map((txn) => ({
                  ...txn,
                  customer: "",
                  id: txn.created_at || "",
                  img: "",
                  transaction_details: txn.transaction_details || {},
                })) || []
              }
              currency={partnerProfile?.payload?.currency_symbol || "₦"}
            />
          </div>
        </div>

        <ChangeWithdrawalBankAccount
          open={openChangeBankModal}
          artisan={true}
          handleClose={toggleChangeBankModal}
          withdrawalBank={artisanDetails?.withdrawal_bank}
          userId={String(artisanDetails._id)}
        />
        <AddNewAccountDetail
          open={addAccount}
          artisan={true}
          handleClose={() => setAddAccount(false)}
          partnerId={partnerId ?? ""}
          addAccount={addAccount}
        />

        <InitiateWithdrawl
          partnerId={partnerId ?? ""}
          open={initiateModalOpen}
          handleClose={() => setInitiateModalOpen(false)}
          setOtpModalOpen={setOtpModalOpen}
        />

        <OtpAdmin
          partnerId={partnerId ?? ""}
          open={otpModalOpen}
          handleClose={() => setOtpModalOpen(false)}
        />
      </div>
      <ToastContainer />
    </DashboardLayout>
  );
};

export default PartnerDetails;
